import React, { Fragment, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useExpanded,
  usePagination,
} from "react-table";
import Spinners from 'components/Common/Spinner';
import { useTranslation } from 'react-i18next';
import { Table, Row, Col, UncontrolledTooltip, Button } from "reactstrap"
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter"
import { useNavigate, Link, useLocation } from "react-router-dom"
import DeleteModal from "./DeleteModal"

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length
  const [value, setValue] = React.useState(globalFilter)
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <React.Fragment>
      <Col md={4}>
        <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={e => {
                  setValue(e.target.value)
                  onChange(e.target.value)
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} records...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      {isJobListGlobalFilter && (
        <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />
      )}
    </React.Fragment>
  )
}

// Loading Skeleton Row Component
const LoadingRow = ({ columnsCount }) => (
  <tr>
    {Array.from({ length: columnsCount + 1 }, (_, index) => (
      <td key={index}>
        <div className="d-flex align-items-center">
          <div
            className="bg-light rounded"
            style={{
              height: "20px",
              width: index === 0 ? "60px" : "80%",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        </div>
      </td>
    ))}
  </tr>
)

// Empty State Component
const EmptyState = ({ columnsCount, message = "No data available" }) => (
  <tr>
    <td colSpan={columnsCount + 1} className="text-center py-5">
      <div className="d-flex flex-column align-items-center">
        <i className="bx bx-data font-size-48 text-muted mb-3"></i>
        <h5 className="text-muted">{message}</h5>
        <p className="text-muted mb-0">There are no records to display</p>
      </div>
    </td>
  </tr>
)

const TableContainer = ({
  addText = "Add New",
  showEdit,
  showDelete,
  showView,
  hasCreate,
  handleEdit,
  handleDelete,
  handleView,
  handleCreate,
  rootPath,
  columns,
  data,
  resource,
  loading,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  isAddUserList,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize = 10,
  customPageSizeOptions,
  iscustomPageSizeOptions,
  isPagination,
  isShowingPageLength,
  paginationDiv,
  pagination,
  paginationData,
  onChangePagination = () => { },
  tableClass,
  theadClass,
  fetchData,
  // Loading props
  isLoading = false,
  loadingRows = 5,
  emptyMessage = "No data available",
  loadingText = "Loading...",
  showActions = true,
  // Server-side pagination props
  isServerSide = false,
  totalRecords = 0,
  currentPage = 0,
  onPageChange,
  onPageSizeChange,
}) => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [id, setId] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Local pagination state for server-side pagination
  const [paginationState, setPaginationState] = useState({
    pageIndex: currentPage,
    pageSize: customPageSize,
  })

  // Update local state when props change
  useEffect(() => {
    setPaginationState({
      pageIndex: currentPage,
      pageSize: customPageSize,
    })
  }, [currentPage, customPageSize])

  const tableData = useMemo(() => data || [], [data])

  // Calculate pagination values for server-side
  const totalPages = isServerSide
    ? Math.ceil(totalRecords / paginationState.pageSize)
    : 0

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: isServerSide ? 0 : paginationState.pageIndex,
        pageSize: paginationState.pageSize,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
      // For server-side pagination, disable react-table's pagination
      manualPagination: isServerSide,
      pageCount: isServerSide ? totalPages : -1,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  )

  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const generateSortingIndicator = column => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""
  }

  const onChangeInSelect = event => {
    const newPageSize = Number(event.target.value)

    if (isServerSide) {
      // Server-side pagination
      setPaginationState(prev => ({
        ...prev,
        pageSize: newPageSize,
        pageIndex: 0, // Reset to first page when changing page size
      }))

      if (onPageSizeChange) {
        onPageSizeChange(newPageSize, 0)
      }
    } else {
      // Client-side pagination
      setPageSize(newPageSize)
    }
  }

  const handlePageChange = newPageIndex => {
    if (isServerSide) {
      setPaginationState(prev => ({
        ...prev,
        pageIndex: newPageIndex,
      }))

      if (onPageChange) {
        onPageChange(newPageIndex, paginationState.pageSize)
      }
    } else {
      gotoPage(newPageIndex)
    }
  }

  const handlePrevious = () => {
    if (isServerSide) {
      const newPageIndex = Math.max(0, paginationState.pageIndex - 1)
      handlePageChange(newPageIndex)
    } else {
      previousPage()
    }
  }

  const handleNext = () => {
    if (isServerSide) {
      const newPageIndex = Math.min(
        totalPages - 1,
        paginationState.pageIndex + 1
      )
      handlePageChange(newPageIndex)
    } else {
      nextPage()
    }
  }

  // Calculate pagination values for display
  const getCurrentPageIndex = () =>
    isServerSide ? paginationState.pageIndex : pageIndex
  const getCurrentPageSize = () =>
    isServerSide ? paginationState.pageSize : pageSize
  const getCanPreviousPage = () =>
    isServerSide ? paginationState.pageIndex > 0 : canPreviousPage
  const getCanNextPage = () =>
    isServerSide ? paginationState.pageIndex < totalPages - 1 : canNextPage
  const getPageOptions = () => {
    if (isServerSide) {
      return Array.from({ length: totalPages }, (_, i) => i)
    }
    return pageOptions
  }

  const displayPageIndex = getCurrentPageIndex()
  const displayPageSize = getCurrentPageSize()
  const totalDisplayRecords = isServerSide ? totalRecords : tableData.length

  const onCreate = () => {
    if (handleCreate) {
      handleCreate()
    } else {
      navigate(`${rootPath || location.pathname}/create`)
    }
  }

  const onEdit = record => {
    if (handleEdit) {
      handleEdit(record)
    } else {
      navigate(`${rootPath || location.pathname}/${record.id}/edit`)
    }
  }

  const onView = record => {
    if (handleView) {
      handleView(record)
    } else {
      navigate(`${rootPath || location.pathname}/${record.id}`)
    }
  }

  const onDelete = record => {
    setId(record?.original?.id)
    setDeleteModal(true)
  }

  const handleDeleteConfirmed = async () => {
    setDeleteLoading(true)
    try {
      await handleDelete(id)
      setDeleteModal(false)

      if (fetchData) {
        fetchData()
      }
    } catch (error) {
      alert(error.message)
    } finally {
      setDeleteLoading(false)
    }
  };


  return (
    <Fragment>
      {/* Add loading indicator CSS */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
          .table-loading-overlay {
            position: relative;
          }
          .table-loading-overlay::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
          }
        `}
      </style>

      <Row className="mb-2">
        {iscustomPageSizeOptions && (
          <Col md={customPageSizeOptions ? 2 : 1}>
            <select
              className="form-select"
              value={getCurrentPageSize()}
              onChange={onChangeInSelect}
              disabled={isLoading}
            >
              {[10, 20, 50, 100, 200].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
          />
        )}

        {hasCreate && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={onCreate}
                disabled={isLoading}
              >
                <i className="mdi mdi-plus me-1" />
                {addText}
              </Button>
            </div>
          </Col>
        )}

        {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={handleUserClick}
                disabled={isLoading}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}

        {isAddCustList && <Col sm="7"></Col>}
      </Row>

      <div className="table-responsive">
        {loading ? (
          <Spinners />
        ) : (<>
          <Table {...getTableProps()} className={tableClass} style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead className={theadClass}>
              {headerGroups.map(headerGroup => (
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th key={column.id} className={column.isSort ? "sorting" : ''}>
                      <div className="m-0" {...column.getSortByToggleProps()}>
                        {column.render("Header")}
                      </div>
                    </th>
                  ))}
                  <th className={''}>
                    <div className="m-0">
                      {"Actions"}
                    </div>
                  </th>
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row)
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr>
                      {row.cells.map(cell => {
                        return (
                          <td key={cell.id} {...cell.getCellProps()} style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {cell.render("Cell")}
                          </td>
                        )
                      })}
                      <td>
                        <div className="d-flex gap-3">
                          {
                            showEdit &&
                            <Button
                              color="link"
                              className="text-success p-0 m-0"
                              onClick={() => onEdit(row)}
                            >
                              <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
                              <UncontrolledTooltip placement="top" target="edittooltip">
                                Edit
                              </UncontrolledTooltip>
                            </Button>
                          }
                          {
                            showView && <Button
                              color="link"
                              className="text-primary p-0 m-0"
                              onClick={() => onView(row)}
                            >
                              <i className="mdi mdi-eye font-size-18" id="viewtooltip" />
                              <UncontrolledTooltip placement="top" target="viewtooltip">
                                View
                              </UncontrolledTooltip>
                            </Button>
                          }
                          {
                            showDelete &&
                            <Button
                              color="link"
                              className="text-danger p-0 m-0"
                              onClick={() => onDelete(row)}
                            >
                              <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
                              <UncontrolledTooltip placement="top" target="deletetooltip">
                                Delete
                              </UncontrolledTooltip>
                            </Button>
                          }
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                )
              })}
            </tbody>
          </Table>
        </>
        )}
      </div>

      {isPagination && (
        <Row className="justify-content-between align-items-center">
          {isShowingPageLength && (
            <div className="col-sm">
              <div className="text-muted">
                Showing{" "}
                <span className="fw-semibold">
                  {isLoading
                    ? 0
                    : Math.min(
                      displayPageIndex * displayPageSize + 1,
                      totalDisplayRecords
                    )}
                </span>{" "}
                to{" "}
                <span className="fw-semibold">
                  {isLoading
                    ? 0
                    : Math.min(
                      (displayPageIndex + 1) * displayPageSize,
                      totalDisplayRecords
                    )}
                </span>{" "}
                of{" "}
                <span className="fw-semibold">
                  {isLoading ? 0 : totalDisplayRecords}
                </span>{" "}
                entries
              </div>
            </div>
          )}
          <div className={paginationDiv}>
            <ul className={pagination}>
              <li
                className={`page-item ${!getCanPreviousPage() || isLoading ? "disabled" : ""
                  }`}
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={isLoading ? undefined : handlePrevious}
                >
                  <i className="mdi mdi-chevron-left"></i>
                </Link>
              </li>
              {getPageOptions().map((item, key) => (
                <React.Fragment key={key}>
                  <li
                    className={
                      displayPageIndex === item
                        ? "page-item active"
                        : "page-item"
                    }
                  >
                    <Link
                      to="#"
                      className={`page-link ${isLoading ? "disabled" : ""}`}
                      onClick={
                        isLoading ? undefined : () => handlePageChange(item)
                      }
                    >
                      {item + 1}
                    </Link>
                  </li>
                </React.Fragment>
              ))}
              <li
                className={`page-item ${!getCanNextPage() || isLoading ? "disabled" : ""
                  }`}
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={isLoading ? undefined : handleNext}
                >
                  <i className="mdi mdi-chevron-right"></i>
                </Link>
              </li>
            </ul>
          </div>
        </Row>
      )}

      <DeleteModal
        id={id}
        show={deleteModal}
        onDeleteClick={handleDeleteConfirmed}
        onCloseClick={() => setDeleteModal(false)}
        loading={deleteLoading}
      />
    </Fragment>
  )
}

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
  isLoading: PropTypes.bool,
  loadingRows: PropTypes.number,
  emptyMessage: PropTypes.string,
  loadingText: PropTypes.string,
  isServerSide: PropTypes.bool,
  totalRecords: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  customPageSize: PropTypes.number,
}

export default TableContainer
